<template>
  <div class="formulario-modelo">

    <LoadingOverlay
      :visible="guardando"
      :message="totalAsignacion > 0
        ? `Asignando indicadores... ${progresoAsignacion} / ${totalAsignacion}`
        : (modoEdicion ? 'Actualizando proceso...' : 'Creando proceso...')"
    />
    <LoadingOverlay :visible="cargandoEdicion" message="Cargando datos del proceso..." />

    <!-- Cabecera -->
    <div class="header-flex">
      <h1>Gestión de Procesos de Autoevaluación</h1>
      <button class="btn salir" @click="$emit('cambiar-modo')">Cambiar modo</button>
    </div>
    <p class="subtitulo">Crea y asigna los procesos de autoevaluación</p>

    <div class="divider"></div>

    <!-- Botón abrir formulario (oculto durante el form) -->
    <div v-if="!mostrarFormulario" class="botones-header">
      <button class="btn guardar" @click="mostrarFormulario = true">Nuevo Proceso</button>
    </div>

    <!-- Formulario de creación / edición -->
    <div v-if="mostrarFormulario" class="marco">

      <h3 class="form-titulo">{{ modoEdicion ? 'Modificar Proceso' : 'Nuevo Proceso' }}</h3>

      <!-- Tipo Proceso -->
      <div class="formulario-opcion">
        <span>Tipo Proceso</span>
        <div class="fila-select-periodo">
          <template v-if="modoEdicion">
            <div class="valor-readonly">
              {{ tipoModeloStore.tipos.find(t => t.id_tipo_modelo === tipoModeloSeleccionado)?.tipo ?? '—' }}
            </div>
          </template>
          <template v-else>
            <select v-model="tipoModeloSeleccionado" class="input-select">
              <option :value="null" disabled>Selecciona un tipo de modelo</option>
              <option v-for="t in tipoModeloStore.tipos" :key="t.id_tipo_modelo" :value="t.id_tipo_modelo">
                {{ t.tipo }}
              </option>
            </select>
          </template>
          <div class="periodo-editor" title="Periodo del proceso (año y semestre)">
            <input
              type="number"
              v-model.number="periodoAnio"
              class="periodo-anio"
              min="2000"
              :max="anioMaximo"
              step="1"
              aria-label="Año del periodo"
            />
            <span class="periodo-sep">-</span>
            <select v-model="periodoSemestre" class="periodo-semestre" aria-label="Semestre del periodo">
              <option value="1">1</option>
              <option value="2">2</option>
            </select>
          </div>
        </div>
      </div>

      <div v-if="tipoModeloSeleccionado !== null" class="formulario-opcion">
        <span>Clase de proceso <b class="req">*</b></span>
        <select v-model="form.id_tipo_ejecucion_modelo" class="input-select">
          <option :value="undefined" disabled>Selecciona para qué se autoevalúa</option>
          <option
            v-for="t in tiposEjecucion"
            :key="t.id_tipo_ejecucion_modelo"
            :value="t.id_tipo_ejecucion_modelo"
          >
            {{ t.tipo }}
          </option>
        </select>
        <small v-if="errores.id_tipo_ejecucion_modelo" class="error-campo">
          {{ errores.id_tipo_ejecucion_modelo }}
        </small>
      </div>

      <!-- Campus para programa (selección única) -->
      <div v-if="tipoModeloSeleccionado !== null && esTipoPrograma(tipoModeloSeleccionado) && campusStore.campus.length" class="formulario-opcion">
        <span>Campus</span>
        <select v-model="form.id_campus" class="input-select">
          <option :value="undefined">— Sin campus específico —</option>
          <option v-for="c in campusStore.campus" :key="c.id_campus" :value="c.id_campus">
            {{ c.nombre }}
          </option>
        </select>
      </div>

      <!-- Campus para institucional (selección múltiple, creación) — va PRIMERO -->
      <div v-if="tipoModeloSeleccionado !== null && !esTipoPrograma(tipoModeloSeleccionado) && !modoEdicion" class="formulario-opcion">
        <span>Campus participantes <b class="req">*</b></span>
        <p v-if="!campusDisponibles.length" class="texto-vacio">No hay campus registrados.</p>
        <div v-else class="campus-grid">
          <label v-for="c in campusDisponibles" :key="c.id_campus" class="campus-item">
            <input type="checkbox" :value="c.id_campus" v-model="instCampus" />
            <span class="campus-nombre">{{ c.nombre }}</span>
          </label>
        </div>
        <small v-if="instCampus.length === 1" class="texto-vacio" style="font-style:italic;">
          Con un solo campus se crea una ejecución individual (sin proceso institucional general).
        </small>
        <small v-if="errores.campus" class="error-campo">{{ errores.campus }}</small>
      </div>

      <!-- Nombre y criterio solo cuando hay 2 o más campus (proceso institucional general) -->
      <template v-if="tipoModeloSeleccionado !== null && !esTipoPrograma(tipoModeloSeleccionado) && !modoEdicion && instCampus.length >= 2">
        <div class="formulario-opcion">
          <span>Nombre del proceso <b class="req">*</b></span>
          <input v-model="instNombre" class="input-select" placeholder="Autoevaluación Institucional 2026-1" />
          <small v-if="errores.nombre" class="error-campo">{{ errores.nombre }}</small>
        </div>
        <div class="formulario-opcion">
          <span>Criterio de consolidación</span>
          <select v-model="instCriterio" class="input-select">
            <option value="ponderado">Ponderado por número de programas</option>
            <option value="simple">Promedio simple (cada campus pesa igual)</option>
          </select>
          <small class="texto-vacio" style="font-style: italic; margin-top: 0.25rem;">
            {{ instCriterio === 'ponderado'
              ? 'Los campus con más programas tienen mayor peso en el consolidado.'
              : 'Todos los campus contribuyen con el mismo peso al consolidado.' }}
          </small>
        </div>
      </template>

      <!-- Área / Programa (aparece si el tipo seleccionado es de programas) -->
      <div v-if="esTipoPrograma(tipoModeloSeleccionado)" class="formulario-opcion">
        <span>Programa</span>
        <select v-model="form.id_programa" class="input-select">
          <option :value="undefined" disabled>Selecciona un programa</option>
          <option v-for="op in areasPlanas" :key="op.id" :value="op.id">{{ op.label }}</option>
        </select>
        <small v-if="errores.id_programa" class="error-campo">{{ errores.id_programa }}</small>
      </div>

      <!-- Versión -->
      <div v-if="tipoModeloSeleccionado !== null" class="formulario-opcion">
        <span>Versión</span>
        <template v-if="modoEdicion">
          <div class="valor-readonly">{{ nombreVersion(form.id_version) }}</div>
        </template>
        <template v-else>
          <div v-if="!versionesFiltradas.length" class="texto-vacio">
            No hay versiones disponibles para este tipo de proceso.
          </div>
          <select v-else v-model="form.id_version" class="input-select">
            <option :value="0" disabled>Selecciona una versión</option>
            <option v-for="v in versionesFiltradas" :key="v.id_version" :value="v.id_version">
              {{ etiquetaVersion(v) }}
            </option>
          </select>
          <small v-if="errores.id_version" class="error-campo">{{ errores.id_version }}</small>
        </template>
      </div>

      <!-- Responsable(s) — aparece cuando hay versión seleccionada -->
      <template v-if="tipoModeloSeleccionado !== null && form.id_version">

        <!-- Caso Programa: un único responsable -->
        <div v-if="esTipoPrograma(tipoModeloSeleccionado)" class="formulario-opcion">
          <span>Responsable del proceso</span>
          <select v-model="usuarioResponsable" class="input-select">
            <option :value="null" disabled>— Selecciona un responsable —</option>
            <option v-for="u in usuarios" :key="u.id" :value="u.id">{{ u.name }}</option>
          </select>
          <small v-if="errores.responsable" class="error-campo">{{ errores.responsable }}</small>
        </div>

        <!-- Caso Institucional (solo edición): responsables por componente -->
        <div v-else-if="modoEdicion" class="formulario-opcion">
          <span>Responsables por componente</span>
          <div v-if="cargandoComponentes" class="loading">Cargando estructura del modelo...</div>
          <template v-else-if="componentesPrimerNivel.length">
            <table class="tabla tabla-asignacion">
              <thead>
                <tr>
                  <th>Componente</th>
                  <th>Responsable</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="comp in componentesPrimerNivel" :key="comp.id_componente">
                  <td>{{ comp.nombre }}</td>
                  <td>
                    <select
                      v-model="responsablesPorComponente[comp.id_componente!]"
                      class="input-select select-usuario"
                    >
                      <option :value="null" disabled>— Selecciona un usuario —</option>
                      <option v-for="u in usuarios" :key="u.id" :value="u.id">{{ u.name }}</option>
                    </select>
                  </td>
                </tr>
              </tbody>
            </table>
          </template>
          <div v-else class="texto-vacio">No se encontraron componentes de primer nivel.</div>
          <small v-if="errores.responsables" class="error-campo">{{ errores.responsables }}</small>
        </div>

      </template>

      <!-- Observaciones -->
      <div v-if="tipoModeloSeleccionado !== null" class="formulario-opcion">
        <span>Observaciones <span class="opcional">(opcional)</span></span>
        <textarea
          v-model="form.observaciones"
          class="formulario-descripcion"
          rows="3"
          placeholder="Notas adicionales sobre el proceso..."
        ></textarea>
      </div>

      <!-- Acciones del formulario -->
      <div class="botones">
        <button class="btn salir" @click="cerrarFormulario">Cancelar</button>
        <button
          v-if="modoEdicion || tipoModeloSeleccionado !== null"
          class="btn guardar"
          :disabled="guardando"
          @click="modoEdicion ? modificarProceso() : crearProceso()"
        >
          {{ guardando ? (modoEdicion ? 'Guardando...' : 'Creando...') : (modoEdicion ? 'Guardar cambios' : 'Crear Proceso') }}
        </button>
      </div>
    </div>

    <!-- Lista de procesos -->
    <div v-if="cargando && !mostrarFormulario" class="loading">Cargando procesos...</div>

    <template v-else-if="!mostrarFormulario">
      <!-- Pestañas de tipo -->
      <div class="proceso-tabs">
        <button
          class="proceso-tab"
          :class="{ 'proceso-tab-activo': filtroTipo === 'programa' }"
          @click="filtroTipo = 'programa'"
        >
          Programas
        </button>
        <button
          class="proceso-tab"
          :class="{ 'proceso-tab-activo': filtroTipo === 'institucional' }"
          @click="filtroTipo = 'institucional'"
        >
          Institucional
        </button>
        <button
          class="proceso-tab"
          :class="{ 'proceso-tab-activo': filtroTipo === 'institucional-general' }"
          @click="filtroTipo = 'institucional-general'; mostrarFormVincular = false"
        >
          Institucional General
        </button>
      </div>

      <!-- ── Pestaña Institucional General ── -->
      <template v-if="filtroTipo === 'institucional-general'">
        <div class="botones-header" style="margin-bottom:1rem;">
          <button class="btn guardar" @click="mostrarFormVincular = true" :disabled="mostrarFormVincular">
            Vincular ejecuciones existentes
          </button>
        </div>

        <p v-if="cargandoInstitucionales" class="loading">Cargando procesos institucionales...</p>
        <p v-else-if="!procesosInstitucionalesFiltrados.length" class="texto-vacio">
          No hay procesos institucionales generales. Crea uno vinculando ejecuciones de campus existentes.
        </p>
        <table v-else class="tabla">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Período</th>
              <th>Versión</th>
              <th>Campus</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="pi in procesosInstitucionalesFiltrados" :key="pi.id_proceso_institucional">
              <td>{{ pi.nombre }}</td>
              <td>{{ pi.periodo }}</td>
              <td>{{ pi.version?.nombre ?? nombreVersion(pi.id_version) }}</td>
              <td>
                <span v-for="e in (pi.ejecuciones ?? [])" :key="e.id_ejecucion" class="chip-campus-inst">
                  {{ e.campus?.nombre ?? '—' }}
                </span>
              </td>
              <td>
                <div class="acciones-fila">
                  <button class="btn guardar btn-tabla" @click="irAResultadosInstitucional(pi)">
                    Resultados
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Formulario: vincular ejecuciones existentes -->
        <div v-if="mostrarFormVincular" class="marco" style="margin-top:1.5rem;">
          <h3 class="form-titulo">Vincular ejecuciones existentes</h3>
          <p class="aviso-version">
            Todas las ejecuciones a vincular deben usar la misma versión del modelo.
            La versión que selecciones aquí determina qué ejecuciones institucionales se muestran disponibles por campus.
          </p>

          <div class="formulario-opcion">
            <span>Nombre <b class="req">*</b></span>
            <input v-model="formVincular.nombre" class="input-select" placeholder="Autoevaluación Institucional 2026-1" />
          </div>

          <div class="fila-form-doble">
            <div class="formulario-opcion">
              <span>Versión <b class="req">*</b></span>
              <select v-model="formVincular.id_version" class="input-select">
                <option :value="0" disabled>Selecciona una versión</option>
                <option v-for="v in versionesStore.versiones" :key="v.id_version" :value="v.id_version">
                  {{ v.nombre }}
                </option>
              </select>
            </div>
            <div class="formulario-opcion">
              <span>Período <b class="req">*</b></span>
              <div style="display:flex;gap:0.4rem;align-items:center;">
                <input type="number" v-model.number="formVincularAnio" class="periodo-anio" min="2000" :max="anioMaximo" step="1" aria-label="Año" />
                <span class="periodo-sep">-</span>
                <select v-model="formVincularSemestre" class="periodo-semestre" aria-label="Semestre">
                  <option value="1">1</option>
                  <option value="2">2</option>
                </select>
              </div>
            </div>
          </div>

          <div class="formulario-opcion">
            <span>Campus a incluir <b class="req">*</b> <small class="opcional">(mínimo 2)</small></span>
            <div class="campus-grid">
              <label v-for="c in campusStore.campus" :key="c.id_campus" class="campus-item">
                <input type="checkbox" :value="c.id_campus" v-model="campusParaVincular" />
                <span class="campus-nombre">{{ c.nombre }}</span>
              </label>
            </div>
            <small v-if="campusParaVincular.length === 1" class="error-campo">
              Se necesitan al menos 2 campus para crear un proceso institucional general.
            </small>
          </div>

          <template v-if="campusParaVincular.length >= 2 && formVincular.id_version && formVincular.periodo">
            <div v-for="grupo in procesosParaVincular" :key="grupo.idCampus" class="formulario-opcion grupo-campus-vincular">
              <span class="grupo-campus-titulo">{{ grupo.campusNombre }}</span>
              <p v-if="!grupo.procesos.length" class="texto-vacio">
                Sin ejecuciones para esta versión y período — se creará una nueva.
              </p>
              <table v-else class="tabla tabla-mini">
                <thead><tr><th>#</th><th>Período</th><th>Estado</th></tr></thead>
                <tbody>
                  <tr v-for="p in grupo.procesos" :key="p.id">
                    <td>{{ p.id }}</td>
                    <td>{{ p.periodo }}</td>
                    <td><span :class="['estado', estadoClass(p.estado)]">{{ p.estado }}</span></td>
                  </tr>
                </tbody>
              </table>
              <small v-if="grupo.procesos.length" class="texto-vacio" style="font-style:italic;">
                Esta ejecución quedará vinculada al proceso institucional general.
              </small>
            </div>
          </template>

          <div class="formulario-opcion">
            <span>Criterio de consolidación</span>
            <select v-model="formVincular.criterio_consolidacion" class="input-select">
              <option value="ponderado">Ponderado por número de programas</option>
              <option value="simple">Promedio simple (cada campus pesa igual)</option>
            </select>
          </div>

          <div class="botones">
            <button class="btn salir" @click="mostrarFormVincular = false; campusParaVincular = []">Cancelar</button>
            <button
              class="btn guardar"
              :disabled="guardandoVincular || !formVincular.nombre.trim() || !formVincular.id_version || campusParaVincular.length < 2"
              @click="crearProcesoDesdeExistentes"
            >
              {{ guardandoVincular ? 'Creando...' : 'Crear proceso general' }}
            </button>
          </div>
        </div>
      </template>

      <!-- ── Pestañas Programas / Institucional ── -->
      <template v-else>

      <!-- Filtros secundarios -->
      <div class="filtros-secundarios">
        <div class="filtro-grupo">
          <label class="filtro-label">Estado:</label>
          <select v-model="filtroEstado" class="select-filtro">
            <option value="">Todos</option>
            <option v-for="e in opcionesEstado" :key="e.value" :value="e.value">{{ e.label }}</option>
          </select>
        </div>
        <div class="filtro-grupo">
          <span class="filtro-label">Buscar:</span>
          <input v-model="busqueda" type="text" class="input-busqueda" placeholder="Período..." />
        </div>
      </div>

      <!-- Tabla de ejecuciones (programas o campus individuales) -->
      <p v-if="!procesosFiltrados.length" class="texto-vacio">
        {{ filtroTipo === 'institucional' ? 'No hay ejecuciones institucionales por campus.' : 'No hay procesos de programa.' }}
      </p>
      <table v-else class="tabla">
        <thead>
          <tr>
            <th>#</th>
            <th>Versión</th>
            <th>Tipo</th>
            <th>{{ filtroTipo === 'institucional' ? 'Campus' : 'Programa' }}</th>
            <th>Período</th>
            <th>Estado</th>
            <th>Observaciones</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="p in procesosFiltrados" :key="p.id">
            <td>{{ p.id }}</td>
            <td>{{ nombreVersion(p.id_version) }}</td>
            <td>
              <span :class="['badge-ambito', p.ambito === 'institucional' ? 'badge-ambito-inst' : 'badge-ambito-prog']">
                {{ p.ambito }}
              </span>
            </td>
            <td>{{
              p.ambito === 'institucional'
                ? (p.campus?.nombre ?? campusStore.campus.find(c => c.id_campus === p.id_campus)?.nombre ?? '—')
                : nombrePrograma(p.id_programa)
            }}</td>
            <td>{{ p.periodo }}</td>
            <td>
              <span :class="['estado', estadoClass(p.estado)]">{{ p.estado ?? '—' }}</span>
            </td>
            <td class="col-obs">{{ p.observaciones || '—' }}</td>
            <td>
              <div class="acciones-fila">
                <button
                  v-if="p.estado === 'preparacion'"
                  class="btn guardar btn-tabla"
                  :disabled="cargandoEdicion"
                  @click="abrirModificarProceso(p)"
                >
                  Modificar
                </button>
                <button
                  v-if="authStore.tienePermiso('procesos.asignar') && p.ambito === 'institucional' && p.estado === 'preparacion'"
                  class="btn guardar btn-tabla"
                  @click="irAAsignarTareas(p.id)"
                >
                  Asignar responsables
                </button>
                <button
                  v-if="p.estado === 'preparacion'"
                  class="btn guardar btn-tabla"
                  :disabled="iniciandoId === p.id"
                  @click="iniciarProceso(p.id!)"
                >
                  {{ iniciandoId === p.id ? 'Iniciando...' : 'Iniciar proceso' }}
                </button>
                <button
                  v-if="authStore.tienePermiso('procesos.asignar') && p.ambito === 'institucional' && ['ejecucion', 'evaluacion'].includes(p.estado ?? '')"
                  class="btn guardar btn-tabla"
                  @click="irAAsignarTareas(p.id)"
                >
                  Asignar tareas
                </button>
                <button
                  v-if="authStore.tienePermiso('calificacion.ver') && ['informe', 'finalizado'].includes(p.estado ?? '') && !esProcesoHistorico(p)"
                  class="btn guardar btn-tabla"
                  @click="irAComparativo(p.id)"
                >
                  Comparativo
                </button>
                <button
                  v-if="authStore.tienePermiso('calificacion.ver') && ['informe', 'finalizado'].includes(p.estado ?? '')"
                  class="btn guardar btn-tabla"
                  @click="irAResultados(p.id)"
                >
                  Resultados
                </button>
                <button
                  v-if="authStore.tienePermiso('informes.ver') && p.estado === 'informe' && !esProcesoHistorico(p)"
                  class="btn guardar btn-tabla"
                  @click="irAInforme(p.id)"
                >
                  Informe
                </button>
                <button
                  v-if="authStore.tienePermiso('planes.ver') && ['informe', 'finalizado'].includes(p.estado ?? '') && !esProcesoHistorico(p)"
                  class="btn guardar btn-tabla"
                  @click="irAPlanMejoramiento(p.id)"
                >
                  Plan de mejoramiento
                </button>
                <button
                  v-if="authStore.tienePermiso('informes.ver') && esProcesoHistorico(p) && ['informe', 'finalizado'].includes(p.estado ?? '')"
                  class="btn guardar btn-tabla"
                  @click="abrirAdjuntarInformes(p)"
                >
                  Adjuntar Informes
                </button>
                <button class="btn salir btn-tabla" @click="confirmarEliminar(p.id)">Eliminar</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      </template>

    </template>

    <!-- Modal confirmación eliminar -->
    <div v-if="procesoAEliminar !== null" class="overlay-modal">
      <div class="modal-confirmacion">
        <p>¿Eliminar el proceso <strong>#{{ procesoAEliminar }}</strong>? Esta acción no se puede deshacer.</p>
        <div class="botones">
          <button class="btn salir" :disabled="eliminando" @click="procesoAEliminar = null">Cancelar</button>
          <button class="btn guardar" :disabled="eliminando" @click="ejecutarEliminar">
            {{ eliminando ? 'Eliminando...' : 'Confirmar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Adjuntar informes de ejecuciones antiguas -->
    <AdjuntarInformesModal
      :visible="adjuntarVisible"
      :idEjecucion="adjuntarIdEjecucion"
      :periodo="adjuntarPeriodo"
      @cerrar="adjuntarVisible = false"
      @subido="alSubirInformes"
    />

    <MensajeConfirmacion
      :visible="mostrarReintentoAsignacion"
      title="Asignaciones incompletas"
      :message="`${asignacionesFallidas.length} asignación(es) no se completaron. ¿Desea volver a intentarlas?`"
      confirmText="Reintentar"
      cancelText="Cancelar"
      :confirmLoading="reintentandoAsignacion"
      @confirm="mostrarReintentoAsignacion = false; reintentarAsignaciones()"
      @cancel="mostrarReintentoAsignacion = false"
    />

    <MensajeFlotante
      :visible="mensajeVisible"
      :mensaje="mensajeTexto"
      :tipo="mensajeTipo"
      @cerrar="mensajeVisible = false"
    />
  </div>
</template>

<script lang="ts" setup>
/**
 * Vista administrativa de las ejecuciones.
 *
 * Vista enrutada. Obtiene sus datos a través de stores Pinia: no llama a `http`
 * ni a los servicios directamente.
 */

import { ref, computed, onMounted, reactive, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'

defineEmits(['cambiar-modo'])

import useAutenticar from '@/store/IngresoAutenticar'
import { useGestionProcesos } from '@/store/GestionProcesoAutoevaluacion'
import { useGestionVersiones } from '@/store/GestionVersiones'
import { useGestionProgramas } from '@/store/GestionPrograma'
import { useGestionTipoModelos } from '@/store/GestionTipoModelo'
import { useGestionCampus } from '@/store/GestionCampus'
import { useGestionIndicadorEvaluadores } from '@/store/GestionIndicadorEvaluadores'
import { useGestionUsuarios } from '@/store/GestionUsuario'
import { useComponentes } from '@/store/GestionComponentes'
import { useGestionIndicadores } from '@/store/GestionIndicadores'
import { useGestionIndicadorInstrumentos } from '@/store/GestionIndicadorInstrumentos'
import { useModelo } from '@/store/GestionModelo'
import { listarUsuarios } from '@/services/RegistroUsuariosService'
import { getTiposEjecucionModeloService } from '@/services/ProcesoAutoevaluacionService'
import {
  getCampusDisponiblesService,
  crearProcesoInstitucionalService,
  listarProcesosInstitucionalesService,
  type CampusDisponible,
  type ProcesoInstitucional,
  type EjecucionDeCampus,
} from '@/services/ProcesoInstitucionalService'
import type { TipoEjecucionModelo } from '@/services/ProcesoAutoevaluacionService'
import type { Proceso } from '@/interfaces/ProcesoAutoevaluacion'
import type { Usuario } from '@/interfaces/Usuario'
import type Componente from '@/interfaces/Componente'
import type Version from '@/interfaces/Versiones'
import MensajeFlotante from '@/components/MensajeFlotante.vue'
import LoadingOverlay from '@/components/LoadingOverlay.vue'
import MensajeConfirmacion from '@/components/MensajeConfirmacion.vue'
import AdjuntarInformesModal from '@/components/AdjuntarInformesModal.vue'
import { crearIndicadorEvaluadorService } from '@/services/IndicadorEvaluadorService'

const router           = useRouter()
const route            = useRoute()
const authStore        = useAutenticar()
const procesosStore    = useGestionProcesos()
const versionesStore   = useGestionVersiones()
const programasStore   = useGestionProgramas()
const tipoModeloStore  = useGestionTipoModelos()
const campusStore      = useGestionCampus()
const evaluadoresStore  = useGestionIndicadorEvaluadores()
const usuariosStore     = useGestionUsuarios()
const componentesStore  = useComponentes()
const indicadoresStore           = useGestionIndicadores()
const indicadorInstrumentoStore  = useGestionIndicadorInstrumentos()
const modeloStore                = useModelo()

const campusDisponibles = ref<CampusDisponible[]>([])
const instNombre   = ref('')
const instCampus   = ref<number[]>([])
const instCriterio = ref<'ponderado' | 'simple'>('ponderado')

// Procesos institucionales generales (ProcesoInstitucional)
const procesosInstitucionales  = ref<ProcesoInstitucional[]>([])
const procesosInstitucionalesFiltrados = computed(() =>
  procesosInstitucionales.value.filter(pi => (pi.ejecuciones?.length ?? 0) >= 2)
)
const cargandoInstitucionales  = ref(false)
const mostrarFormVincular      = ref(false)
const guardandoVincular        = ref(false)
const campusParaVincular       = ref<number[]>([])
const formVincular = reactive({
  nombre:                 '',
  id_version:             0,
  periodo:                calcularPeriodo(),
  criterio_consolidacion: 'ponderado' as 'ponderado' | 'simple',
  observaciones:          '',
})

interface AsignacionFallida {
  id_version:      number
  id_ejecucion:    number
  id_indicador:    number
  id_usuario:      number
  id_asignado_por: number | null
}

const tipoModeloSeleccionado = ref<number | null>(null)

const cargando                   = ref(false)
const guardando                  = ref(false)
const progresoAsignacion         = ref(0)
const totalAsignacion            = ref(0)
const mostrarFormulario          = ref(false)
const procesoAEliminar           = ref<number | null>(null)
// Adjuntar informes de ejecuciones antiguas
const adjuntarVisible            = ref(false)
const adjuntarIdEjecucion        = ref<number | null>(null)
const adjuntarPeriodo            = ref<string | null>(null)
const iniciandoId                = ref<number | null>(null)
const eliminando                 = ref(false)
const asignacionesFallidas       = ref<AsignacionFallida[]>([])
const mostrarReintentoAsignacion = ref(false)
const reintentandoAsignacion     = ref(false)
const mensajeVisible    = ref(false)
const mensajeTexto      = ref('')
const mensajeTipo       = ref<'positivo' | 'error'>('positivo')
const filtroEstado      = ref('')
const filtroTipo        = ref<'programa' | 'institucional' | 'institucional-general'>(
  route.query.tab === 'institucional-general' ? 'institucional-general'
  : route.query.tab === 'institucional' ? 'institucional'
  : 'programa'
)
const busqueda          = ref('')
const usuarios = ref<Usuario[]>([])

// Modo edición
const modoEdicion       = ref(false)
const procesoEnEdicion  = ref<Proceso | null>(null)
const cargandoEdicion   = ref(false)

// Formulario — responsables al crear/editar proceso
const usuarioResponsable           = ref<number | null>(null)
const componentesPrimerNivel       = ref<Componente[]>([])
const responsablesPorComponente    = reactive<Record<number, number | null>>({})
const cargandoComponentes          = ref(false)

// Estado inicial capturado al abrir el formulario de edición (para detectar cambios)
const responsableInicialPrograma         = ref<number | null>(null)
const responsablesInicialesComponentes   = reactive<Record<number, number | null>>({})


async function cargarComponentesPrimerNivel(idVersion: number): Promise<void> {
  cargandoComponentes.value = true
  try {
    const version = await versionesStore.getVersionById(idVersion) as Version | null
    const idModelo = version?.id_modelo
    if (!idModelo) return

    const raiz = await componentesStore.cargarRaizPorModelo(idModelo)
    if (!raiz?.id_componente) return
    await componentesStore.cargarArbolDesdeRaiz(raiz.id_componente)

    const raizNodo = componentesStore.componentes.find(c => c.id_componente === raiz.id_componente)
    if (!raizNodo?.children?.length) return

    componentesPrimerNivel.value = [...raizNodo.children].sort((a, b) =>
      (a.ordinal ?? '').localeCompare(b.ordinal ?? '', undefined, { numeric: true })
    )
    for (const comp of raizNodo.children) {
      if (comp.id_componente != null) responsablesPorComponente[comp.id_componente] = null
    }
  } catch {
    mostrarMensaje('Error al cargar la estructura del modelo.', 'error')
  } finally {
    cargandoComponentes.value = false
  }
}

function recopilarHojasDeRama(comp: Componente): Componente[] {
  if (!comp.children?.length) return [comp]
  const hojas: Componente[] = []
  for (const hijo of comp.children) hojas.push(...recopilarHojasDeRama(hijo))
  return hojas
}

async function asignarEnParalelo(asignaciones: AsignacionFallida[], tamanioLote = 20): Promise<AsignacionFallida[]> {
  if (!asignaciones.length) return []
  totalAsignacion.value = asignaciones.length
  progresoAsignacion.value = 0
  const fallidas: AsignacionFallida[] = []
  for (let i = 0; i < asignaciones.length; i += tamanioLote) {
    const lote = asignaciones.slice(i, i + tamanioLote)
    const resultados = await Promise.allSettled(
      lote.map(a => crearIndicadorEvaluadorService({
        id_version:      a.id_version,
        id_ejecucion:    a.id_ejecucion,
        id_indicador:    a.id_indicador,
        id_usuario:      a.id_usuario,
        id_asignado_por: a.id_asignado_por
      }))
    )
    lote.forEach((a, j) => {
      const r = resultados[j]
      if (r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.ok)) fallidas.push(a)
    })
    progresoAsignacion.value += lote.length
  }
  return fallidas
}

async function asignarIndicadoresPorRama(
  idVersion: number,
  comp: Componente,
  idUsuario: number,
  idAsignadoPor: number | null,
  idEjecucion: number
): Promise<AsignacionFallida[]> {
  const hojas = recopilarHojasDeRama(comp)
  const asignaciones: AsignacionFallida[] = []
  for (const hoja of hojas) {
    if (!hoja.id_componente) continue
    const indicadores = indicadoresStore.indicadores.filter(
      i => i.id_componente === hoja.id_componente && i.id_version === idVersion && i.id_indicador != null
    )
    for (const ind of indicadores) {
      asignaciones.push({ id_version: idVersion, id_ejecucion: idEjecucion, id_indicador: ind.id_indicador!, id_usuario: idUsuario, id_asignado_por: idAsignadoPor })
    }
  }
  return asignarEnParalelo(asignaciones)
}

const opcionesEstado = [
  { label: 'Preparación', value: 'preparacion' },
  { label: 'Ejecución',   value: 'ejecucion'   },
  { label: 'Evaluación',  value: 'evaluacion'  },
  { label: 'Informe',     value: 'informe'     },
  { label: 'Finalizado',  value: 'finalizado'  }
]

function calcularPeriodo(): string {
  const now  = new Date()
  const year = now.getFullYear()
  return (now.getMonth() + 1) <= 6 ? `${year}-1` : `${year}-2`
}

function esProcesoHistorico(p: Proceso): boolean {
  if (p.periodo !== calcularPeriodo()) return true
  const idsIndicadores = new Set(
    indicadoresStore.indicadores
      .filter(i => i.id_version === p.id_version && i.id_indicador != null)
      .map(i => i.id_indicador as number)
  )
  if (idsIndicadores.size === 0) return false
  return !indicadorInstrumentoStore.relaciones.some(r => idsIndicadores.has(r.id_indicador))
}

const form = reactive<Omit<Proceso, 'id' | 'estado'>>({
  ambito:        '' as any,
  id_version:    0,
  periodo:       calcularPeriodo(),
  id_programa:   undefined,
  id_campus:     undefined,
  id_tipo_ejecucion_modelo: undefined,
  observaciones: ''
})

/** Catálogo de tipos de proceso (acreditación, reacreditación, renovación). */
const tiposEjecucion = ref<TipoEjecucionModelo[]>([])

const errores = reactive<Record<string, string>>({})

// Editor de periodo (formato "YYYY-S"): permite fijar periodos anteriores
// para procesos antiguos en lugar de tomar siempre el periodo actual.
const anioMaximo = new Date().getFullYear() + 1

const periodoAnio = computed<number>({
  get: () => {
    const n = parseInt(form.periodo.split('-')[0], 10)
    return Number.isFinite(n) ? n : new Date().getFullYear()
  },
  set: (v) => { form.periodo = `${v || new Date().getFullYear()}-${periodoSemestre.value}` }
})

const periodoSemestre = computed<string>({
  get: () => (form.periodo.split('-')[1] === '1' ? '1' : '2'),
  set: (v) => { form.periodo = `${periodoAnio.value}-${v}` }
})

function cerrarFormulario() {
  mostrarFormulario.value = false
  resetForm()
}

function resetForm() {
  form.ambito              = '' as any
  form.id_version          = 0
  form.periodo             = calcularPeriodo()
  form.id_programa         = undefined
  form.id_campus           = undefined
  form.id_tipo_ejecucion_modelo = undefined
  form.observaciones       = ''
  tipoModeloSeleccionado.value      = null
  usuarioResponsable.value          = null
  componentesPrimerNivel.value      = []
  modoEdicion.value                 = false
  procesoEnEdicion.value            = null
  for (const k in responsablesPorComponente) delete (responsablesPorComponente as any)[k]
  campusDisponibles.value = []
  instNombre.value   = ''
  instCampus.value   = []
  instCriterio.value = 'ponderado'
  mostrarFormVincular.value = false
  campusParaVincular.value  = []
  Object.keys(errores).forEach(k => delete errores[k])
}

async function abrirModificarProceso(proceso: Proceso) {
  resetForm()
  modoEdicion.value      = true
  procesoEnEdicion.value = proceso
  cargandoEdicion.value  = true
  mostrarFormulario.value = true

  const esPrograma = proceso.ambito === 'programa'
  const tipoEncontrado = tipoModeloStore.tipos.find(t =>
    esPrograma
      ? t.tipo.trim().toLowerCase().startsWith('programa')
      : !t.tipo.trim().toLowerCase().startsWith('programa')
  )
  tipoModeloSeleccionado.value = tipoEncontrado?.id_tipo_modelo ?? null

  form.ambito        = proceso.ambito
  form.id_version    = proceso.id_version
  form.id_campus     = proceso.id_campus
  form.id_programa   = proceso.id_programa
  form.periodo       = proceso.periodo
  form.id_tipo_ejecucion_modelo = proceso.id_tipo_ejecucion_modelo ?? undefined
  form.observaciones = proceso.observaciones ?? ''

  try {
    if (!usuarios.value.length) {
      const [usrs] = await Promise.all([listarUsuarios(), usuariosStore.me()])
      usuarios.value = usrs
    }

    if (proceso.id_campus) {
      await programasStore.cargarTodos(proceso.id_campus)
    }

    await cargarComponentesPrimerNivel(proceso.id_version)
    await evaluadoresStore.cargar({ id_version: proceso.id_version, id_ejecucion: proceso.id })

    if (esPrograma) {
      const asig = evaluadoresStore.asignaciones.find(a => a.id_indicador != null)
      usuarioResponsable.value = asig?.id_usuario ?? null
      responsableInicialPrograma.value = usuarioResponsable.value
    } else {
      if (!indicadoresStore.indicadores.length) await indicadoresStore.cargarTodos()
      for (const comp of componentesPrimerNivel.value) {
        if (!comp.id_componente) continue
        const hojas = recopilarHojasDeRama(comp)
        let idUsuario: number | null = null
        for (const hoja of hojas) {
          if (!hoja.id_componente || idUsuario) continue
          const inds = indicadoresStore.indicadores.filter(
            i => i.id_componente === hoja.id_componente && i.id_version === proceso.id_version
          )
          for (const ind of inds) {
            const asig = evaluadoresStore.asignaciones.find(a => a.id_indicador === ind.id_indicador)
            if (asig?.id_usuario) { idUsuario = asig.id_usuario; break }
          }
        }
        responsablesPorComponente[comp.id_componente] = idUsuario
      }
      // Capturar estado inicial para detectar cambios al guardar
      for (const k in responsablesInicialesComponentes) delete (responsablesInicialesComponentes as any)[k]
      for (const comp of componentesPrimerNivel.value) {
        if (comp.id_componente != null)
          responsablesInicialesComponentes[comp.id_componente] = responsablesPorComponente[comp.id_componente] ?? null
      }
    }
  } catch {
    mostrarMensaje('Error al cargar los datos del proceso.', 'error')
  } finally {
    cargandoEdicion.value = false
  }
}

async function modificarProceso() {
  if (!procesoEnEdicion.value?.id || !validarForm()) return
  guardando.value = true

  const id            = procesoEnEdicion.value.id
  const idEjecucion   = id!
  const esPrograma    = esTipoPrograma(tipoModeloSeleccionado.value)
  const idVersion     = form.id_version
  const idAsignadoPor = authStore.usuarioActual?.id ?? null
  const proc          = procesoEnEdicion.value

  // Detectar qué cambió
  const formCambiado =
    (form.id_campus  ?? null) !== (proc.id_campus  ?? null) ||
    (esPrograma && (form.id_programa ?? null) !== (proc.id_programa ?? null)) ||
    (form.observaciones?.trim() || null) !== (proc.observaciones?.trim() || null) ||
    form.periodo !== proc.periodo

  const responsableProgramaCambio = esPrograma &&
    usuarioResponsable.value !== responsableInicialPrograma.value

  const responsablesInstCambio = !esPrograma &&
    componentesPrimerNivel.value.some(c =>
      c.id_componente != null &&
      responsablesPorComponente[c.id_componente] !== responsablesInicialesComponentes[c.id_componente]
    )

  const asignacionesCambiaron = responsableProgramaCambio || responsablesInstCambio

  if (!formCambiado && !asignacionesCambiaron) {
    guardando.value = false
    cerrarFormulario()
    mostrarMensaje('No se detectaron cambios.', 'positivo')
    return
  }

  // PATCH solo si cambiaron campos del proceso
  if (formCambiado) {
    const result = await procesosStore.actualizar(id, {
      id_campus:     form.id_campus,
      id_programa:   esPrograma ? form.id_programa : undefined,
      observaciones: form.observaciones?.trim() || undefined,
      periodo:       form.periodo,
    })
    if (!result.ok) {
      guardando.value = false
      const msgs = result.errors ? Object.values(result.errors).flat().join(', ') : 'Error al actualizar el proceso'
      mostrarMensaje(msgs, 'error')
      return
    }
  }

  const todasFallidasMod: AsignacionFallida[] = []

  // Operaciones de asignación solo si cambiaron responsables
  if (asignacionesCambiaron) {
    await evaluadoresStore.cargar({ id_version: idVersion, id_ejecucion: idEjecucion })

    if (esPrograma) {
      if (usuarioResponsable.value) {
        const asignacionesActuales = evaluadoresStore.asignaciones.filter(a => a.id_indicador != null && a.id != null)
        await Promise.allSettled(asignacionesActuales.map(a => evaluadoresStore.eliminar(a.id!)))
        for (const comp of componentesPrimerNivel.value) {
          todasFallidasMod.push(...await asignarIndicadoresPorRama(idVersion, comp, usuarioResponsable.value, idAsignadoPor, idEjecucion))
        }
      }
    } else {
      const eliminaciones: number[] = []
      const nuevasAsignaciones: AsignacionFallida[] = []

      for (const comp of componentesPrimerNivel.value) {
        if (!comp.id_componente) continue
        const idUsuario = responsablesPorComponente[comp.id_componente]
        if (!idUsuario) continue
        const hojas = recopilarHojasDeRama(comp)
        for (const hoja of hojas) {
          if (!hoja.id_componente) continue
          const inds = indicadoresStore.indicadores.filter(
            i => i.id_componente === hoja.id_componente && i.id_version === idVersion && i.id_indicador != null
          )
          for (const ind of inds) {
            const existente = evaluadoresStore.asignaciones.find(a => a.id_indicador === ind.id_indicador)
            if (existente?.id_usuario === idUsuario) continue
            if (existente?.id) eliminaciones.push(existente.id)
            nuevasAsignaciones.push({ id_version: idVersion, id_ejecucion: idEjecucion, id_indicador: ind.id_indicador!, id_usuario: idUsuario, id_asignado_por: idAsignadoPor })
          }
        }
      }

      await Promise.allSettled(eliminaciones.map(id => evaluadoresStore.eliminar(id)))
      todasFallidasMod.push(...await asignarEnParalelo(nuevasAsignaciones))
    }
  }

  if (todasFallidasMod.length > 0) {
    const reintento = await asignarEnParalelo(todasFallidasMod)
    todasFallidasMod.length = 0
    todasFallidasMod.push(...reintento)
  }

  totalAsignacion.value = 0
  guardando.value = false
  cerrarFormulario()
  await procesosStore.cargarTodos()

  if (todasFallidasMod.length > 0) {
    asignacionesFallidas.value = todasFallidasMod
    mostrarReintentoAsignacion.value = true
    mostrarMensaje(`Proceso actualizado, pero ${todasFallidasMod.length} asignación(es) no se completaron.`, 'error')
  } else {
    mostrarMensaje('Proceso actualizado correctamente.', 'positivo')
  }
}

const idsVersionConProcesoActivo = computed(() =>
  new Set(
    procesosStore.procesos
      .filter(p => p.estado !== 'finalizado' && p.id_version != null)
      .map(p => p.id_version as number)
  )
)

const versionesFiltradas = computed(() => {
  if (tipoModeloSeleccionado.value === null) return []
  const modeloTipoMap = new Map(
    modeloStore.modelos.map(m => [m.id_modelo, m.id_tipo_modelo])
  )
  // Los procesos institucionales pueden correr en paralelo para campus distintos,
  // así que no se bloquea la versión aunque ya exista un proceso activo.
  const esPrograma = esTipoPrograma(tipoModeloSeleccionado.value)
  return versionesStore.versiones.filter(v => {
    const estadoOk =
      v.estado === 'aprobado' ||
      (v.estado === 'edicion' && (!esPrograma || !idsVersionConProcesoActivo.value.has(v.id_version!)))
    if (!estadoOk) return false
    return modeloTipoMap.get(v.id_modelo) === tipoModeloSeleccionado.value
  })
})

const nombresRepetidosVersion = computed(() => {
  const counts = new Map<string, number>()
  for (const v of versionesFiltradas.value) {
    counts.set(v.nombre, (counts.get(v.nombre) ?? 0) + 1)
  }
  return new Set([...counts.entries()].filter(([, n]) => n > 1).map(([nombre]) => nombre))
})

function etiquetaVersion(v: Version): string {
  if (!nombresRepetidosVersion.value.has(v.nombre)) return v.nombre
  const raw = v.fecha_aprobacion ?? v.fecha_creacion
  const d = new Date(raw)
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const aa = String(d.getFullYear()).slice(-2)
  return `${v.nombre} · ${dd}/${mm}/${aa}`
}

function esTipoPrograma(idTipo: number | null): boolean {
  if (idTipo === null) return false
  const tipo = tipoModeloStore.tipos.find(t => t.id_tipo_modelo === idTipo)
  return !!tipo && tipo.tipo.trim().toLowerCase().startsWith('programa')
}

watch(mostrarFormulario, async (abierto) => {
  if (abierto && !usuarios.value.length) {
    const [usrs] = await Promise.all([listarUsuarios(), usuariosStore.me()])
    usuarios.value = usrs
  }
})

watch(tipoModeloSeleccionado, async (idTipo) => {
  if (modoEdicion.value) return
  form.id_version  = 0
  form.id_programa = undefined
  form.id_campus   = undefined
  componentesPrimerNivel.value = []
  campusDisponibles.value = []
  instCampus.value = []
  for (const k in responsablesPorComponente) delete (responsablesPorComponente as any)[k]
  usuarioResponsable.value = null
  if (idTipo !== null && !esTipoPrograma(idTipo)) {
    campusDisponibles.value = await getCampusDisponiblesService()
  }
})

watch(() => form.id_campus, async (idCampus) => {
  if (modoEdicion.value) return
  form.id_programa = undefined
  await programasStore.cargarTodos(idCampus ?? undefined)
})

watch(() => form.id_version, () => {
  if (modoEdicion.value) return
  componentesPrimerNivel.value = []
  for (const k in responsablesPorComponente) delete (responsablesPorComponente as any)[k]
})

const areasPlanas = computed(() =>
  programasStore.programas
    .filter(p => p.nivel === 'pregrado' || p.nivel === 'posgrado')
    .map(p => ({ id: p.id_programa as number, label: p.nombre }))
)

function mostrarMensaje(texto: string, tipo: 'positivo' | 'error') {
  mensajeTexto.value   = texto
  mensajeTipo.value    = tipo
  mensajeVisible.value = true
}

const procesosOrdenados = computed(() =>
  [...procesosStore.procesos].sort((a, b) => (b.id ?? 0) - (a.id ?? 0))
)

const procesosFiltrados = computed(() => {
  let base = procesosOrdenados.value
  if (filtroEstado.value) base = base.filter(p => p.estado === filtroEstado.value)
  if (filtroTipo.value && filtroTipo.value !== 'institucional-general')
    base = base.filter(p => p.ambito === filtroTipo.value)
  if (busqueda.value.trim()) {
    const t = busqueda.value.trim().toLowerCase()
    base = base.filter(p => p.periodo.toLowerCase().includes(t))
  }
  return base
})

// ── ProcesoInstitucional general ─────────────────────────────────────

async function cargarProcesosInstitucionales(): Promise<void> {
  cargandoInstitucionales.value = true
  procesosInstitucionales.value = await listarProcesosInstitucionalesService()
  cargandoInstitucionales.value = false
}


function irAResultadosInstitucional(pi: ProcesoInstitucional): void {
  router.push({ name: 'ProcesosInstitucionales', query: { id: String(pi.id_proceso_institucional) } })
}

// Período del formulario de vinculación
const formVincularAnio = computed<number>({
  get: () => {
    const n = parseInt(formVincular.periodo.split('-')[0], 10)
    return Number.isFinite(n) ? n : new Date().getFullYear()
  },
  set: (v) => { formVincular.periodo = `${v || new Date().getFullYear()}-${formVincularSemestre.value}` }
})

const formVincularSemestre = computed<string>({
  get: () => (formVincular.periodo.split('-')[1] === '1' ? '1' : '2'),
  set: (v) => { formVincular.periodo = `${formVincularAnio.value}-${v}` }
})

// Procesos existentes por campus que coinciden con versión+período seleccionados
const procesosParaVincular = computed(() => {
  if (campusParaVincular.value.length < 2 || !formVincular.id_version || !formVincular.periodo) return []
  return campusParaVincular.value.map(idCampus => {
    const procesos = procesosStore.procesos.filter(p =>
      p.id_campus === idCampus &&
      p.ambito === 'institucional' &&
      p.id_version === formVincular.id_version &&
      p.periodo === formVincular.periodo
    )
    const campusNombre = campusStore.campus.find(c => c.id_campus === idCampus)?.nombre ?? `Campus ${idCampus}`
    return { idCampus, campusNombre, procesos }
  })
})

async function crearProcesoDesdeExistentes(): Promise<void> {
  if (!formVincular.nombre.trim() || !formVincular.id_version || campusParaVincular.value.length < 2) return
  guardandoVincular.value = true
  const result = await crearProcesoInstitucionalService({
    nombre:    formVincular.nombre.trim(),
    periodo:   formVincular.periodo,
    id_version: formVincular.id_version,
    criterio_consolidacion: formVincular.criterio_consolidacion,
    observaciones: formVincular.observaciones || null,
    campus: campusParaVincular.value,
  })
  guardandoVincular.value = false
  if (!result.ok) {
    mostrarMensaje(result.error ?? 'Error al crear el proceso institucional', 'error')
    return
  }
  mostrarFormVincular.value = false
  campusParaVincular.value = []
  Object.assign(formVincular, { nombre: '', id_version: 0, periodo: calcularPeriodo(), criterio_consolidacion: 'ponderado', observaciones: '' })
  await Promise.all([procesosStore.cargarTodos(), cargarProcesosInstitucionales()])
  mostrarMensaje('Proceso institucional general creado y procesos vinculados.', 'positivo')
}

function nombreVersion(idVersion: number): string {
  const version = versionesStore.versiones.find(v => v.id_version === idVersion)
  return version?.nombre ?? `#${idVersion}`
}

function nombrePrograma(idPrograma?: number | null): string {
  if (!idPrograma) return '—'
  const programa = programasStore.programas.find(p => p.id_programa === idPrograma)
  return programa?.nombre ?? `#${idPrograma}`
}

function irAComparativo(id?: number) {
  if (!id) {
    mostrarMensaje('No se pudo identificar la ejecución.', 'error')
    return
  }
  router.push({ name: 'ComparacionCalificaciones', params: { id_ejecucion: id } })
}

function irAResultados(id?: number) {
  if (!id) {
    mostrarMensaje('No se pudo identificar la ejecución.', 'error')
    return
  }
  router.push({ name: 'ResultadosAutoevaluacion', params: { id_ejecucion: id } })
}

function irAInforme(id?: number) {
  if (!id) {
    mostrarMensaje('No se pudo identificar la ejecución.', 'error')
    return
  }
  router.push({ name: 'InformeAutoevaluacion', params: { id_ejecucion: id } })
}

function irAPlanMejoramiento(id?: number) {
  if (!id) {
    mostrarMensaje('No se pudo identificar la ejecución.', 'error')
    return
  }
  router.push({ name: 'PlanMejoramiento', params: { id_ejecucion: id } })
}

function irAAsignarTareas(id?: number) {
  if (!id) {
    mostrarMensaje('No se pudo identificar la ejecución.', 'error')
    return
  }
  router.push({ name: 'AsignarTareasDirector', params: { id_ejecucion: id } })
}

function estadoClass(estado?: string | null) {
  const map: Record<string, string> = {
    preparacion: 'estado-modificar',
    ejecucion:   'estado-completado',
    evaluacion:  'estado-aprobado',
    informe:     'estado-aprobado',
    finalizado:  'estado-descontinuado'
  }
  return map[estado ?? ''] ?? 'estado-modificar'
}

function validarForm(): boolean {
  Object.keys(errores).forEach(k => delete errores[k])
  if (!form.id_version) errores.id_version = 'Selecciona una versión'
  // El backend también lo exige; validarlo aquí evita el viaje y el 422.
  if (!form.id_tipo_ejecucion_modelo)
    errores.id_tipo_ejecucion_modelo = 'Indica para qué se autoevalúa'
  const esPrograma = esTipoPrograma(tipoModeloSeleccionado.value)
  if (esPrograma && !form.id_programa)
    errores.id_programa = 'Selecciona un programa'
  if (esPrograma && !usuarioResponsable.value)
    errores.responsable = 'Selecciona un responsable'
  if (!esPrograma && !modoEdicion.value) {
    if (instCampus.value.length === 0)
      errores.campus = 'Selecciona al menos un campus'
    if (instCampus.value.length >= 2 && !instNombre.value.trim())
      errores.nombre = 'Ingresa un nombre para el proceso'
  }
  if (!esPrograma && modoEdicion.value) {
    const sinAsignar = componentesPrimerNivel.value.filter(
      c => c.id_componente != null && !responsablesPorComponente[c.id_componente!]
    ).length
    if (sinAsignar > 0)
      errores.responsables = `Faltan ${sinAsignar} responsable(s) por asignar`
  }
  return Object.keys(errores).length === 0
}

async function crearProceso() {
  if (!validarForm()) return
  guardando.value = true
  const esPrograma = esTipoPrograma(tipoModeloSeleccionado.value)

  // Institucional — rama según cantidad de campus
  if (!esPrograma) {
    if (instCampus.value.length === 1) {
      // Un solo campus → ejecución individual (sin ProcesoInstitucional)
      const result = await procesosStore.crear({
        ambito:       'institucional',
        id_version:   form.id_version,
        periodo:      form.periodo,
        id_campus:    instCampus.value[0],
        id_tipo_ejecucion_modelo: form.id_tipo_ejecucion_modelo,
        observaciones: form.observaciones?.trim() || undefined,
      })
      guardando.value = false
      if (!result.ok) {
        const msgs = result.errors ? Object.values(result.errors).flat().join(', ') : 'Error al crear el proceso'
        mostrarMensaje(msgs, 'error')
        return
      }
      cerrarFormulario()
      await procesosStore.cargarTodos()
      mostrarMensaje('Proceso institucional creado correctamente.', 'positivo')
      return
    }

    // Dos o más campus → ProcesoInstitucional general
    const result = await crearProcesoInstitucionalService({
      nombre:    instNombre.value.trim(),
      periodo:   form.periodo,
      id_version: form.id_version,
      id_tipo_ejecucion_modelo: form.id_tipo_ejecucion_modelo ?? null,
      criterio_consolidacion: instCriterio.value,
      observaciones: form.observaciones?.trim() || null,
      campus: instCampus.value,
    })
    guardando.value = false
    if (!result.ok) {
      mostrarMensaje(result.error ?? 'Error al crear el proceso institucional', 'error')
      return
    }
    cerrarFormulario()
    await Promise.all([procesosStore.cargarTodos(), cargarProcesosInstitucionales()])
    mostrarMensaje('Proceso institucional general creado correctamente.', 'positivo')
    return
  }

  // Programa — flujo con asignación de responsable
  const idVersion   = form.id_version
  const payload: Omit<Proceso, 'id' | 'estado'> = {
    ambito:        'programa',
    id_version:    idVersion,
    periodo:       form.periodo,
    id_programa:   form.id_programa,
    id_campus:     form.id_campus ?? undefined,
    id_tipo_ejecucion_modelo: form.id_tipo_ejecucion_modelo,
    observaciones: form.observaciones?.trim() || undefined
  }

  const result = await procesosStore.crear(payload)
  if (!result.ok || !result.data) {
    guardando.value = false
    const msgs = result.errors ? Object.values(result.errors).flat().join(', ') : 'Error al crear el proceso'
    mostrarMensaje(msgs, 'error')
    return
  }

  const idEjecucion   = result.data.id!
  const idAsignadoPor = authStore.usuarioActual?.id ?? null
  const todasFallidas: AsignacionFallida[] = []

  const version = await versionesStore.getVersionById(idVersion) as Version | null
  const idModelo = version?.id_modelo
  if (idModelo) {
    const raiz = await componentesStore.cargarRaizPorModelo(idModelo)
    if (raiz?.id_componente) await componentesStore.cargarArbolDesdeRaiz(raiz.id_componente)
  }
  const raizNodo = componentesStore.componentes[0]
  for (const comp of raizNodo?.children ?? []) {
    todasFallidas.push(...await asignarIndicadoresPorRama(idVersion, comp, usuarioResponsable.value!, idAsignadoPor, idEjecucion))
  }

  if (todasFallidas.length > 0) {
    const reintento = await asignarEnParalelo(todasFallidas)
    todasFallidas.length = 0
    todasFallidas.push(...reintento)
  }

  totalAsignacion.value = 0
  guardando.value = false
  cerrarFormulario()
  await procesosStore.cargarTodos()

  if (todasFallidas.length > 0) {
    asignacionesFallidas.value = todasFallidas
    mostrarReintentoAsignacion.value = true
    mostrarMensaje(`Proceso creado, pero ${todasFallidas.length} asignación(es) no se completaron.`, 'error')
  } else {
    mostrarMensaje('Proceso creado y asignado correctamente. Puede iniciarlo cuando esté listo.', 'positivo')
  }
}

async function reintentarAsignaciones() {
  reintentandoAsignacion.value = true
  const fallidas = await asignarEnParalelo([...asignacionesFallidas.value])
  asignacionesFallidas.value = fallidas
  reintentandoAsignacion.value = false
  if (fallidas.length === 0) {
    mostrarMensaje('Todas las asignaciones completadas correctamente.', 'positivo')
  } else {
    mostrarReintentoAsignacion.value = true
  }
}

async function iniciarProceso(id: number) {
  if (iniciandoId.value !== null) return
  const proceso = procesosStore.procesos.find(p => p.id === id)
  if (!proceso?.id_version) {
    mostrarMensaje('No se pudo obtener la versión del proceso.', 'error')
    return
  }
  iniciandoId.value = id
  try {
    const idsIndicadoresVersion = new Set(
      indicadoresStore.indicadores
        .filter(i => i.id_version === proceso.id_version && i.id_indicador != null)
        .map(i => i.id_indicador as number)
    )

    if (proceso.ambito === 'institucional') {
      await evaluadoresStore.cargar({ id_ejecucion: id })
      const conCalificar = new Set(
        evaluadoresStore.asignaciones
          .filter(a => a.tarea === 'calificar' && a.id_indicador != null && idsIndicadoresVersion.has(a.id_indicador))
          .map(a => a.id_indicador as number)
      )
      const sinAsignar = [...idsIndicadoresVersion].filter(idInd => !conCalificar.has(idInd))
      if (sinAsignar.length > 0) {
        mostrarMensaje(
          `No se puede iniciar: ${sinAsignar.length} indicador${sinAsignar.length === 1 ? '' : 'es'} sin responsable de calificación. Usa "Asignar responsables" primero.`,
          'error'
        )
        return
      }
    }

    const tieneInstrumentos = indicadorInstrumentoStore.relaciones
      .some(r => idsIndicadoresVersion.has(r.id_indicador))

    // El backend solo acepta preparacion → ejecucion como primer paso.
    // Si la versión no tiene instrumentos no hay fase de subida de evidencias:
    // se avanza inmediatamente a evaluacion independientemente de si es continuación.
    const saltarEjecucion = !tieneInstrumentos

    const step1 = await procesosStore.cambiarEstado(id, 'ejecucion')
    if (!step1.ok) {
      mostrarMensaje('No se pudo iniciar el proceso.', 'error')
      return
    }

    if (saltarEjecucion) {
      const step2 = await procesosStore.cambiarEstado(id, 'evaluacion')
      if (!step2.ok) {
        mostrarMensaje('No se pudo avanzar el proceso a evaluación.', 'error')
        await procesosStore.cargarTodos()
        return
      }
    }

    mostrarMensaje('Proceso iniciado correctamente.', 'positivo')
    await procesosStore.cargarTodos()
  } finally {
    iniciandoId.value = null
  }
}

// Adjuntar los informes que se generaron en su momento para una ejecución antigua.
function abrirAdjuntarInformes(p: Proceso) {
  if (!p.id) {
    mostrarMensaje('No se pudo identificar la ejecución.', 'error')
    return
  }
  adjuntarIdEjecucion.value = p.id
  adjuntarPeriodo.value = p.periodo ?? null
  adjuntarVisible.value = true
}

function alSubirInformes(cantidad: number) {
  mostrarMensaje(
    cantidad === 1 ? 'Archivo adjuntado correctamente.' : `${cantidad} archivos adjuntados correctamente.`,
    'positivo'
  )
}

function confirmarEliminar(id: number | undefined) {
  if (!id) {
    mostrarMensaje('No se pudo identificar el proceso.', 'error')
    return
  }
  procesoAEliminar.value = id
}

async function ejecutarEliminar() {
  if (procesoAEliminar.value === null || eliminando.value) return
  const id = procesoAEliminar.value
  procesoAEliminar.value = null
  eliminando.value = true
  try {
    const result = await procesosStore.eliminar(id)
    if (result.ok) {
      mostrarMensaje('Proceso eliminado.', 'positivo')
      await procesosStore.cargarTodos()
    } else {
      const msg = result.errors
        ? Object.values(result.errors).flat()[0] ?? 'No se pudo eliminar el proceso.'
        : 'No se pudo eliminar el proceso.'
      mostrarMensaje(msg, 'error')
    }
  } finally {
    eliminando.value = false
  }
}

onMounted(async () => {
  cargando.value = true
  await Promise.all([
    procesosStore.cargarTodos(),
    versionesStore.getVersiones(),
    programasStore.cargarTodos(),
    tipoModeloStore.getTipoModelos(),
    campusStore.cargarTodos(),
    indicadoresStore.cargarTodos(),
    indicadorInstrumentoStore.getRelaciones(),
    modeloStore.getModelo(),
    getTiposEjecucionModeloService().then(t => { tiposEjecucion.value = t }),
    cargarProcesosInstitucionales(),
  ])
  cargando.value = false
})
</script>

<style scoped>
.formulario-modelo {
  background-color: white;
  padding: var(--space-lg);
  border-radius: var(--radius-lg);
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  box-shadow: var(--shadow-md);
  box-sizing: border-box;
  margin: 20px;
  font-family: var(--font-family);
}

h1 {
  font-weight: 700;
  letter-spacing: 0.01em;
  line-height: 1.3;
  margin: 0;
}

.subtitulo {
  font-size: var(--font-size-sm, 0.875rem);
  color: #555;
  margin: 0.25rem 0 0;
}

.header-flex {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.divider {
  border: none;
  border-bottom: 1px solid #e0e0e0;
  margin: 1rem 0;
}

.botones-header { margin-bottom: 1.25rem; }

.form-titulo {
  margin: 0 0 0.5rem;
  font-size: 1rem;
  font-weight: 700;
  color: #1a1a1a;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e0e0e0;
}

.valor-readonly {
  padding: 0.5rem 0.75rem;
  background: #f5f5f5;
  border: 1px solid #e6e5e5;
  border-radius: var(--radius-md);
  font-size: 0.95rem;
  color: #333;
  font-weight: 600;
  min-height: 42px;
  display: flex;
  align-items: center;
  flex: 1;
}

.marco {
  border: 2px solid rgb(156, 155, 155);
  box-sizing: border-box;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.formulario-opcion {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.formulario-opcion > span {
  color: #8f8e8e;
  font-size: var(--font-size-sm, 0.875rem);
}

.opcional { font-size: 0.8rem; color: #bbb; }

.fila-select-periodo {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.fila-select-periodo .input-select {
  flex: 1;
}

.input-select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e6e5e5;
  border-radius: var(--radius-md);
  font-size: 1rem;
  font-family: var(--font-family);
  background-color: #f9f9f9;
  height: auto;
  min-height: 42px;
  line-height: 1.4;
  appearance: auto;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.input-select:focus {
  outline: none;
  border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1);
}

.periodo-editor {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-shrink: 0;
}

.periodo-anio {
  width: 5.5rem;
  padding: 0.4rem 0.6rem;
  border: 1px solid #e6e5e5;
  border-radius: var(--radius-md);
  font-size: 0.95rem;
  font-family: var(--font-family);
  background-color: #f9f9f9;
  color: #333;
  font-weight: 600;
  text-align: center;
}

.periodo-sep {
  color: #8f8e8e;
  font-weight: 700;
}

.periodo-semestre {
  padding: 0.4rem 0.6rem;
  border: 1px solid #e6e5e5;
  border-radius: var(--radius-md);
  font-size: 0.95rem;
  font-family: var(--font-family);
  background-color: #f9f9f9;
  color: #333;
  font-weight: 600;
  cursor: pointer;
}

.periodo-anio:focus,
.periodo-semestre:focus {
  outline: none;
  border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1);
}

.formulario-descripcion {
  padding: 0.75rem;
  border: 1px solid #e6e5e5;
  border-radius: var(--radius-md);
  font-size: 1rem;
  font-family: var(--font-family);
  resize: vertical;
  background-color: #f9f9f9;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.formulario-descripcion:focus {
  outline: none;
  border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1);
}

.error-campo { font-size: 0.78rem; color: #b00020; }

.card-proceso-asig {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: var(--color-background-soft, #f0f4f8);
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 10px;
  padding: 1rem 1.25rem;
  margin-bottom: 1.25rem;
}

@media (prefers-color-scheme: dark) {
  .card-proceso-asig { background: rgba(255,255,255,0.06); border-color: rgba(255,255,255,0.1); }
}
:root[data-theme="dark"] .card-proceso-asig { background: rgba(255,255,255,0.06); border-color: rgba(255,255,255,0.1); }
:root[data-theme="light"] .card-proceso-asig { background: #f0f4f8; border-color: rgba(0,0,0,0.08); }

.card-proceso-info {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  flex: 1;
}

.card-proceso-nombre {
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.3;
}

.card-proceso-area {
  font-size: 0.85rem;
  opacity: 0.65;
}

.asig-responsable-bloque {
  margin-bottom: 1rem;
}

.asig-responsable-label {
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
}

.asig-responsable-fila {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
}

.panel-asig-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.panel-asig-titulo {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.15rem;
}

.select-usuario {
  min-height: 36px;
  padding: 0.3rem 0.6rem;
  font-size: 0.875rem;
  width: 100%;
  min-width: 200px;
}

.td-indicador {
  max-width: 300px;
  word-break: break-word;
  white-space: normal;
}

.tabla-asignacion td {
  vertical-align: middle;
  padding: 0.5rem 0.75rem;
}

.campus-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 0.5rem;
  margin-top: 0.25rem;
}

.campus-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e6e5e5;
  border-radius: var(--radius-md);
  cursor: pointer;
  background: #f9f9f9;
  font-size: 0.875rem;
  transition: background 0.15s;
}

.campus-item:hover {
  background: #f0f4f8;
  border-color: #0e69a0;
}

.campus-item input[type="checkbox"] {
  width: 15px;
  height: 15px;
  cursor: pointer;
  accent-color: #0e69a0;
  flex-shrink: 0;
}

.campus-nombre {
  font-size: 0.875rem;
  color: #1a1a1a;
  flex: 1;
}

.campus-programas {
  font-size: 0.72rem;
  color: #8f8e8e;
  white-space: nowrap;
}

.inst-general-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.75rem;
  padding: 0.85rem 1.1rem;
  border: 1px solid #c5d9ee;
  border-radius: var(--radius-md);
  background: #f4f8fd;
}

.inst-general-info {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.inst-general-titulo {
  font-size: 0.9rem;
  font-weight: 700;
  color: #1a56a0;
}

.inst-general-desc {
  font-size: 0.8rem;
  color: #6b7280;
}

.inst-general-acciones {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.btn-sm {
  padding: 0.35rem 0.85rem;
  font-size: 0.82rem;
}

.chip-campus-inst {
  display: inline-block;
  padding: 2px 8px;
  margin: 0 3px 3px 0;
  border-radius: 999px;
  background: #eef5fb;
  color: #0e69a0;
  font-size: 0.75rem;
  font-weight: 600;
}

.estado-por-campus {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8rem;
  margin-bottom: 2px;
}

.estado-campus-nombre {
  color: #6b7280;
  font-size: 0.78rem;
}

.aviso-version {
  font-size: 0.82rem;
  color: #1a56a0;
  background: #eef5fb;
  border: 1px solid #c5d9ee;
  border-radius: var(--radius-md);
  padding: 0.5rem 0.85rem;
  margin-bottom: 1rem;
}

.estado-mini {
  display: block;
  font-size: 0.75rem;
  margin-bottom: 2px;
  color: #555;
}

.fila-form-doble {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.grupo-campus-vincular {
  border: 1px solid #e6e5e5;
  border-radius: var(--radius-md);
  padding: 0.75rem 1rem;
  background: #fafafa;
}

.grupo-campus-titulo {
  font-weight: 700;
  font-size: 0.875rem;
  color: #0e69a0;
}

.tabla-mini th,
.tabla-mini td {
  padding: 0.35rem 0.6rem;
  font-size: 0.82rem;
}

.avance-estado {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background-color: #f0f8e8;
  border: 1px solid #b5d99c;
  border-radius: var(--radius-md);
}

.avance-texto {
  margin: 0;
  font-size: 0.9rem;
  color: #2d6a0f;
  font-weight: 500;
}

/* ── Filtros ── */
.proceso-tabs {
  display: flex;
  gap: 0;
  border-bottom: 2px solid #e0e0e0;
  margin-bottom: 1.25rem;
}

.proceso-tab {
  padding: 0.55rem 1.25rem;
  font-size: 0.9rem;
  font-weight: 600;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  color: #6b7280;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
}

.proceso-tab:hover {
  color: #0e69a0;
}

.proceso-tab-activo {
  color: #0e69a0;
  border-bottom-color: #0e69a0;
}

.filtros-secundarios {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
  align-items: center;
}

.filtro-grupo { display: flex; align-items: center; gap: 0.4rem; }
.filtro-label { font-size: 0.85rem; font-weight: 600; color: #333; white-space: nowrap; }

.select-filtro {
  padding: 0.35rem 0.65rem;
  border-radius: 8px;
  border: 1px solid #ccc;
  font-size: 0.85rem;
  background: #f9f9f9;
  font-family: var(--font-family);
}

.input-busqueda {
  padding: 0.35rem 0.8rem;
  border-radius: 16px;
  border: 1px solid #ccc;
  font-size: 0.85rem;
  width: 180px;
  font-family: var(--font-family);
  background: #f9f9f9;
}

.select-filtro:focus, .input-busqueda:focus {
  outline: none;
  border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1);
}

.tabla { width: 100%; border-collapse: collapse; font-size: 0.9rem; margin-top: 0.5rem; }
.tabla thead tr { background-color: #f5f5f5; }
.tabla th {
  padding: 0.65rem 1rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
}
.tabla td { padding: 0.6rem 1rem; border-bottom: 1px solid #f0f0f0; vertical-align: middle; }
.tabla tbody tr:hover { background-color: #f9f9f9; }
.col-obs { max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #777; }

.estado {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  display: inline-block;
  letter-spacing: 0.03em;
  text-transform: capitalize;
}

.estado-modificar     { background-color: #eea222; color: #000; }
.estado-aprobado      { background-color: #d1f3d1; color: #1a7f1a; }
.estado-completado    { background-color: #0e69a0; color: white; }
.estado-descontinuado { background-color: #e0e0e0; color: #666; }

.acciones-fila { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }

.btn-tabla { padding: 0.3rem 0.75rem; font-size: 0.8rem; height: auto; }

.botones {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
}

.btn {
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  height: 40px;
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  transition: all 0.15s ease;
  display: inline-flex;
  align-items: center;
}

.btn:disabled { opacity: 0.5; cursor: not-allowed; }

.btn.guardar { background-color: #eea222; color: black; }
.btn.guardar:hover:not(:disabled) { background-color: #0e69a0; color: white; }

.btn.salir { background-color: white; border: 1px solid #ccc; color: #333; }
.btn.salir:hover:not(:disabled) { background-color: rgb(212, 26, 26); color: white; }

.btn.accion { background-color: #0e69a0; color: white; }
.btn.accion:hover:not(:disabled) { background-color: #0b5580; color: white; }

.overlay-modal {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-confirmacion {
  background: white;
  border-radius: var(--radius-lg);
  padding: 2rem;
  max-width: 400px;
  width: 90%;
  box-shadow: var(--shadow-md);
}

.modal-confirmacion p { font-size: 0.95rem; color: #333; margin-bottom: 1rem; }
.modal-confirmacion .botones { margin-top: 0; }

.loading     { padding: 1rem; color: #333; }
.texto-vacio { padding: 1rem 0; color: #8f8e8e; font-size: 0.9rem; }

/* ── Badge de tipo (institucional / programa) ── */
.badge-ambito {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.15);
}

.badge-ambito-inst { background: linear-gradient(135deg, #0e69a0 0%, #1a8dcf 100%); }
.badge-ambito-prog { background: linear-gradient(135deg, #c47a00 0%, #eea222 100%); }
</style>
